import * as React from "react";
import { cn } from "../../lib/utils";

/*
   FAQ Scroll Accordion — Self-Contained Scroll Cascade.

   A scroll-aware FAQ inside its OWN scrollable container.
   The IntersectionObserver is scoped to the container, NOT
   the page viewport — items only respond to scrolling
   WITHIN the component, never to the main page scroll.

   IntersectionObserver { root: scrollContainer }
     rootMargin: "-40% 0px -40% 0px"
     -> center detection zone = middle 20% of container
     -> an item must reach the container's center to trigger

   Scroll UX:
     Click: opens item + smoothly scrolls container to center
            it, then pauses scroll-driven for 3s
     Scroll: items cascade open as they reach center zone

   Zero dependencies. No GSAP, no framer-motion, no lucide.
*/

export function FAQScrollAccordion({
  title = "Frequently asked questions",
  subtitle = "Everything you need to know.",
  items,
  scrollDriven = true,
  defaultActive = 0,
  className,
}) {
  const [active, setActive] = React.useState(defaultActive);
  const itemRefs = React.useRef([]);
  const scrollPausedRef = React.useRef(false);
  const pauseTimerRef = React.useRef(undefined);

  // Callback ref for the scroll container — triggers re-render
  // when mounted so the IntersectionObserver effect can use it.
  const [scrollContainer, setScrollContainer] = React.useState(null);

  // Scroll-driven auto-open
  // Scoped to the scroll container, NOT the page viewport.
  // rootMargin "-40% 0px -40% 0px" = center 20% of container.
  React.useEffect(() => {
    if (!scrollDriven || !scrollContainer) return;

    const observers = [];

    itemRefs.current.forEach((el, i) => {
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (scrollPausedRef.current) return;
          if (entry.isIntersecting) {
            setActive(i);
          }
        },
        {
          root: scrollContainer,
          rootMargin: "-40% 0px -40% 0px",
          threshold: 0.5,
        },
      );

      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [scrollDriven, scrollContainer, items]);

  // Click handler
  // Opens item, scrolls container to center it, then pauses
  // scroll-driven behavior for 3s so the observer doesn't
  // immediately override the user's selection.
  const toggle = React.useCallback(
    (i) => {
      scrollPausedRef.current = true;
      setActive((prev) => {
        const next = prev === i ? null : i;

        // Smooth-scroll the container to center the clicked item
        if (next !== null && scrollContainer) {
          const el = itemRefs.current[next];
          if (el) {
            requestAnimationFrame(() => {
              const scrollTop =
                el.offsetTop -
                scrollContainer.clientHeight / 2 +
                el.offsetHeight / 2;
              scrollContainer.scrollTo({
                top: Math.max(0, scrollTop),
                behavior: "smooth",
              });
            });
          }
        }

        return next;
      });

      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = setTimeout(() => {
        scrollPausedRef.current = false;
      }, 3000);
    },
    [scrollContainer],
  );

  React.useEffect(() => {
    return () => {
      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    };
  }, []);

  return (
    <section className={cn("py-16 md:py-24", className)}>
      <div className="mx-auto max-w-2xl px-6">
        {/* Header (outside scroll container) */}
        {title && (
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-semibold tracking-tight text-foreground">
              {title}
            </h2>
            {subtitle && (
              <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
        )}

        {/* Scrollable container */}
        <div
          ref={setScrollContainer}
          className="relative max-h-80 overflow-y-auto"
          data-faq-scroll
          style={{
            maskImage:
              "linear-gradient(to bottom, transparent, black 5%, black 95%, transparent)",
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent, black 5%, black 95%, transparent)",
            scrollbarWidth: "none",
          }}
        >
          {items.map((item, i) => {
            const isActive = i === active;

            return (
              <div
                key={i}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                className={cn("border-b border-border", i === 0 && "border-t")}
              >
                <button
                  type="button"
                  className="flex w-full items-center justify-between py-5 text-left"
                  onClick={() => toggle(i)}
                >
                  <span
                    className="pr-4 text-base font-medium transition-opacity duration-300"
                    style={{ opacity: isActive ? 1 : 0.5 }}
                  >
                    {item.question}
                  </span>

                  {/* Chevron — inline SVG, no lucide */}
                  <svg
                    className="h-4 w-4 shrink-0 text-muted-foreground"
                    style={{
                      transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                      transition:
                        "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
                    }}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>

                {/* Expandable answer */}
                <div
                  className="grid"
                  style={{
                    gridTemplateRows: isActive ? "1fr" : "0fr",
                    transition:
                      "grid-template-rows 0.45s cubic-bezier(0.16, 1, 0.3, 1)",
                  }}
                >
                  <div className="overflow-hidden">
                    <p
                      className="pb-5 text-sm leading-relaxed text-muted-foreground"
                      style={{
                        opacity: isActive ? 1 : 0,
                        transform: isActive
                          ? "translateY(0)"
                          : "translateY(8px)",
                        transition: isActive
                          ? "opacity 0.35s cubic-bezier(0.16, 1, 0.3, 1) 0.1s, transform 0.35s cubic-bezier(0.16, 1, 0.3, 1) 0.1s"
                          : "opacity 0.15s ease, transform 0.15s ease",
                      }}
                    >
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Hide webkit scrollbar */}
      <style
        dangerouslySetInnerHTML={{
          __html: `[data-faq-scroll]::-webkit-scrollbar{display:none}`,
        }}
      />
    </section>
  );
}
